import React from 'react';
import { getRoutes } from 'utils/auth';
import { NotFoundPage } from 'app/components/NotFoundPage';

/**
 * Checks if a resource exists in user's resource_mappings
 * @param resourceIdentifier - Can be resource name, component name, or path
 * @returns boolean
 */
export const hasResource = (resourceIdentifier: string): boolean => {
  try {
    const resourceMappings = getRoutes() || [];
    if (!Array.isArray(resourceMappings) || resourceMappings.length === 0) {
      return false;
    }
    
    return resourceMappings.some(
      (resource) => 
        resource?.name === resourceIdentifier || 
        resource?.component === resourceIdentifier ||
        resource?.path === resourceIdentifier
    );
  } catch (error) {
    console.error('Error checking resource access:', error);
    return false;
  }
};

/**
 * HOC to protect components based on resource_mappings
 * @param Component - Component to protect
 * @param resourceIdentifier - Resource name/component/path to check
 * @param fallback - Optional fallback component (default: NotFoundPage)
 */
export const withResourceProtection = <P extends object>(
  Component: React.ComponentType<P>,
  resourceIdentifier: string,
  fallback?: React.ReactNode
) => {
  const ProtectedComponent = React.forwardRef<any, P>((props, ref) => {
    const hasAccess = hasResource(resourceIdentifier);
    
    if (!hasAccess) {
      // Use provided fallback or default to NotFoundPage
      const FallbackComponent = fallback !== undefined ? fallback : <NotFoundPage />;
      return FallbackComponent as React.ReactElement;
    }
    
    // Forward ref if component supports it
    if (ref) {
      return <Component {...props} ref={ref} />;
    }
    
    return <Component {...props} />;
  });
  
  ProtectedComponent.displayName = `withResourceProtection(${Component.displayName || Component.name || 'Component'})`;
  
  return ProtectedComponent;
};
